import { useCallback, useMemo } from "react";
import { generatePath } from "react-router";

import { cn } from "@/lib/chillnn/cn";
import type { SnackBlockItemType } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";
import type {
	SelectableSnackItemType,
	SnackBlocksFormModelContentsMethodsType,
} from "@/components/features/snack/snack/settings/model/usecase/use-create-snack-layout-settings-form-model/methods/contents";

import { Button } from "@/components/ui/button/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ThreeDotsHorizontal from "@/components/ui/icon/three-dots-horizontal";

import { DialogSelectSnack } from "../../../dialog/snack";

import { useSelectSnackModalState } from ".";

type Props = {
	hotelID: string;
	index: number;

	blockUUID: string;

	snack: ForAutoGenerator<
		{
			title: string;
			itemType: SnackBlockItemType;
			itemUUID: string;
			thumbnail: string;
		},
		""
	>;

	contentsMethods: SnackBlocksFormModelContentsMethodsType;
	heightTailwindStyle: string;
};

export const LayoutSettingsIslandContentRowSnackItem = ({
	hotelID,
	blockUUID,
	index,
	snack,
	contentsMethods,
	heightTailwindStyle,
}: Props) => {
	const { onRemoveSnackFromBlock, selectableSnacks, onAddSnackToBlock } = contentsMethods;
	const {
		activateSelectSnackModal,
		deactivateSelectSnackModal,
		isShowSelectSnackModal,
		handleOpenChangeSelectSnackModal,
	} = useSelectSnackModalState();

	// スナックが選択されている場合
	const { itemUUID, thumbnail, title } = snack;
	const bgImageStyle = thumbnail?.value ? `url('${thumbnail.value}')` : "none";

	// 編集可能かどうか
	const isEditable = Boolean(itemUUID.value);
	const handleClickNavigateToEditPageButton = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
		(e) => {
			e.stopPropagation();
			const path = generatePath(PATHS.HOTEL.RESERVATION_PAGE.SNACK.EDIT.UUID, {
				hotelID,
				uuid: itemUUID.value,
			});
			window.open(path, "_blank");
		},
		[hotelID, itemUUID.value],
	);

	const handleClickRemoveSnackFromBlockButton = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
		(e) => {
			e.stopPropagation();
			onRemoveSnackFromBlock({ blockUUID, snackUUID: itemUUID.value });
		},
		[blockUUID, itemUUID.value, onRemoveSnackFromBlock],
	);

	const handleClickSelectOtherSnackButton = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
		(e) => {
			e.stopPropagation();
			activateSelectSnackModal();
		},
		[activateSelectSnackModal],
	);

	const showBorder = useMemo(() => !thumbnail.value, [thumbnail.value]);

	return (
		<div
			className={cn(
				"relative",
				"bg-cover bg-center bg-no-repeat", //
				"rounded-[20px]",
				showBorder ? "border" : "",
				heightTailwindStyle,
			)}
			style={{
				backgroundImage: bgImageStyle,
			}}
		>
			<DropdownMenu>
				<DropdownMenuTrigger>
					<Button variant={"input-ish"} size={"icon"} className="absolute left-1.5 top-1.5" asChild>
						<ThreeDotsHorizontal size={"xs"} />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent className="w-54" sideOffset={26}>
					<div className="grid space-y-1 text-left">
						{isEditable && (
							<Button
								variant={"no-outline"}
								textAlign={"left"}
								onClick={handleClickNavigateToEditPageButton}
							>
								スナックを編集
							</Button>
						)}
						<Button
							variant={"no-outline"}
							textAlign={"left"}
							onClick={handleClickRemoveSnackFromBlockButton}
						>
							<p className="font-medium">このスナックを外す</p>
						</Button>
						<Button variant={"no-outline"} textAlign={"left"} onClick={handleClickSelectOtherSnackButton}>
							<p className="font-medium">他のスナックを選択する</p>
						</Button>
					</div>
				</DropdownMenuContent>
			</DropdownMenu>
			<p className="absolute bottom-1 left-2 text-white">{title.value}</p>

			<DialogSelectSnack
				hotelID={hotelID}
				onSelectSnack={({ selectedSnack }: { selectedSnack: SelectableSnackItemType }) =>
					onAddSnackToBlock({ blockUUID, snackIndex: index, selectedSnack })
				}
				open={isShowSelectSnackModal}
				handleClickCloseButton={deactivateSelectSnackModal}
				deactivateSelectSnackModal={deactivateSelectSnackModal}
				handleOpenChange={handleOpenChangeSelectSnackModal}
				selectableSnacks={selectableSnacks}
			/>
		</div>
	);
};

LayoutSettingsIslandContentRowSnackItem.displayName = "LayoutSettingsIslandContentRowSnackItem";
